
import { PatchError } from '@archipad-js/core/error';
import { AttachmentSerializer, PatchHandlerService, SerializedJSONAttachment } from '@archipad-js/core/patch';
import { PatchesDescriptor, PatchDescriptor, PatchAttachmentDescriptor } from '@archipad/services/file/patchFile';
import { CacheFileDescriptor } from '@core/drivers/cacheFile/browser/cacheFileDescriptor';
import { IllegalArgumentError } from '@core/errors/errors-core';

// Regex to parse localCache urls : userId/projectId/entityId/attachmentKey
const CACHE_PATH_REGEX = /^localCache:([^\/]+)\/([^\/]+)\/([^\/]+)\/([^\/]+)$/;

/**
 * Temp patch attachment serializer
 * 
 * transforms "temp:" and "patch:" urls into PatchFileDescriptor 
 */
export class TempPatchAttachmentSerializer implements AttachmentSerializer {
    private _tempPatch: PatchDescriptor;
    private _patchesDir: PatchesDescriptor;

    private _patchesById: Map<string, PatchDescriptor>;

    constructor(projectId:string) {
        const tempDir = new PatchesDescriptor(projectId, "temp");
        const patchesDir = new PatchesDescriptor(projectId, "patch");
        
        this._tempPatch = new PatchDescriptor(tempDir, "current"); // temp patch attachments are all located in patch named "current"
        this._patchesDir = patchesDir;
        this._patchesById = new Map();
    }

    private _getPatch(patchId:string) {
        let patch = this._patchesById.get(patchId);
        if(!patch) {
            patch = new PatchDescriptor(this._patchesDir, patchId);
            this._patchesById.set(patchId, patch)
        }

        return patch;
    }

    public get directory(): PatchDescriptor {
        return this._tempPatch;
    }

    /**
     * Compatibility with old attachment format: attempt to guess the file descriptor type from the url
     * 
     * @param url 
     */
    private _oldUrlCompatibility(url:string): PatchAttachmentDescriptor {
        let ret = url.match(/^[^\/]+\/projects\/[0-9a-f]{16}\/([^\/]+)\/([0-9]+)\/(.*)$/);
        if(ret) {
            // url is in the form: 215-4f86e3394bc76-bigsool/projects/61fa643498ea1f57/00000154aa262c48.0ab59388/7060065542923231063/reports/levée_des_réserves__b80__r1_ouest--1462260445-0/report.xlsx
            // this is a patch file
            const patch = this._getPatch(ret[1]);
            return new PatchAttachmentDescriptor(patch, ret[2], ret[3]);
        }

        ret = url.match(/^[^\/]+\/assets\/projects\/[0-9a-f]{32}\/current\/([0-9]+)\/(.*)$/);
        if(ret) {
            // url is in the form: 215-4f86e3394bc76-bigsool/assets/projects/000000000000000047b1f203d06c9dc1/current/907175279255307749/qwe.pdf
            // this is a temp patch file
            return new PatchAttachmentDescriptor(this._tempPatch, ret[1], ret[2]);
        }

        ret = url.match(/^[^\/]+\/assets\/projects\/[0-9a-f]{32}\/users\/[0-9]+\/current\/([0-9]+)\/(.*)$/);
        if(ret) {
            // url is in the form: 215-4f86e3394bc76-bigsool/assets/projects/000000000000000047b1f203d06c9dc1/users/20476/current/907175279255307749/qwe.pdf
            // this is a temp patch file
            return new PatchAttachmentDescriptor(this._tempPatch, ret[1], ret[2]);
        }

        throw new PatchError(`Invalid temp patch url: "${url}"`);

    }

    serializeAttachment(file: PatchAttachmentDescriptor | CacheFileDescriptor, entityId:string, attachmentKey:string): SerializedJSONAttachment {
        if(!(file instanceof PatchAttachmentDescriptor))
            throw new IllegalArgumentError(`Invalid attachment file`);
        
        const json:SerializedJSONAttachment = { url:null };
        if(file.type === "temp") {
            json.url = `temp:${file.entityId}/${file.attachmentPath}`
        } else if(file.type === "patch") {
            json.url = `patch:${file.patch.patchId}/${file.entityId}/${file.attachmentPath}`
        } else if(file.type === "localCache") {
            if (!(file instanceof CacheFileDescriptor)){
                throw new IllegalArgumentError(`Invalid attachment file`);
            }
            json.url = `localCache:${file.userId}/${file.projectId}/${file.entityId}/${file.attachmentKey}`;
        } else {
            throw new IllegalArgumentError(`Invalid attachment file`);
        }
        if (json.url === null){
            throw new PatchError('Invalid file type in temp patch')
        }   
        
        else 

        if(file.mimeType)
            json.mimeType = file.mimeType;
        if(file.fileSize)
            json.size = file.fileSize;
        if(file.hash)
            json.hash = file.hash;

        return json;
    }

    deserializeAttachment(json:SerializedJSONAttachment, entityId:string, attachmentKey:string): PatchAttachmentDescriptor {
        let ret: RegExpMatchArray;
        let file:PatchAttachmentDescriptor;

        ret = json.url.match(/^temp:([0-9]+)\/(.*)$/);
        if(ret) {
            // temp url
            file = new PatchAttachmentDescriptor(this._tempPatch, ret[1], ret[2]);
        } else if (json.url.match(CACHE_PATH_REGEX)) {
            // localCache url
            const matches = json.url.match(CACHE_PATH_REGEX);
            const [
                ,userId, projectId, entityId, attachmentKey
            ] = matches;
            file = new CacheFileDescriptor(
                new PatchDescriptor(
                    new PatchesDescriptor(
                        projectId,
                        "localCache"
                    ),
                    "current"
                ),
                entityId,
                attachmentKey,
                projectId,
                userId,
            );
        } else {
            ret = json.url.match(/^patch:([^\/]+)\/([0-9]+)\/(.*)$/);
            if(ret) {
                // patch url
                const patch = this._getPatch(ret[1]);
                file = new PatchAttachmentDescriptor(patch, ret[2], ret[3]);
            } else {
                // compatibility with current temp patches
                return this._oldUrlCompatibility(json.url);
            }
        }

        if(json.mimeType)
            file.mimeType = json.mimeType;
        if(json.size)
            file.fileSize = json.size;
        if(json.hash)
            file.hash = json.hash;

        return file;
    }
}

// TODO @deprecated, use DI instead
export default new PatchHandlerService();